/**
 * Binds the shortcut registry to the keyboard (one window listener) and
 * renders the `?` sheet from the same registry, so what the sheet lists
 * is exactly what is bound.
 *
 * Single keys never fire while typing in a field or while a dialog is open
 * (BUILD-PLAN 3.5: "focus-gated, never inside inputs"); chords with
 * Ctrl/Cmd/Alt do, so Ctrl+K works everywhere.
 */
import { ShortcutSheet, type ShortcutSection } from '@velora/ui';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import {
  controlOwnsKey,
  isTypingTarget,
  menuTarget,
  ShortcutMatcher,
  shortcutLabels,
  type Binding,
} from './keys.js';
import { isMacPlatform } from './platform.js';
import { shortcutRegistry, sortSections, type RegisteredShortcut } from './registry.js';

function dialogOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
    ) !== null
  );
}

export function ShortcutListener() {
  const isMac = isMacPlatform();
  const matcher = useRef(new ShortcutMatcher<RegisteredShortcut>(isMac));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || e.repeat) return;
      // A focused button, tab or slider keeps Enter, Space and the arrows.
      if (!e.ctrlKey && !e.metaKey && !e.altKey && controlOwnsKey(e.target, e.key)) return;
      const entries = shortcutRegistry.get().filter((s) => s.run && !s.displayOnly);
      const bindings: Binding<RegisteredShortcut>[] = entries.map((s) => ({
        shortcut: s.parsed,
        allowInInputs: s.allowInInputs ?? false,
        value: s,
      }));
      const typing = isTypingTarget(e.target) || dialogOpen() || menuTarget(e.target);
      const result = matcher.current.feed(e, bindings, performance.now(), typing);
      if (result.kind === 'match') {
        e.preventDefault();
        result.binding.value.run?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return null;
}

export function ShortcutSheetHost({
  open,
  onOpenChange,
  screenName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenName?: string;
}) {
  const entries = useSyncExternalStore(
    shortcutRegistry.subscribe,
    shortcutRegistry.get,
    shortcutRegistry.get,
  );
  const isMac = isMacPlatform();
  const sections = useMemo<ShortcutSection[]>(() => {
    const names = sortSections(entries.map((e) => e.section));
    return names.map((name) => ({
      id: name,
      title: name === 'This screen' && screenName ? screenName : name,
      shortcuts: entries
        .filter((e) => e.section === name)
        .map((e) => ({
          id: e.id,
          label: e.description,
          keys: shortcutLabels(e.keys, isMac),
          sequence: e.parsed.steps.length > 1,
        })),
    }));
  }, [entries, isMac, screenName]);
  return (
    <ShortcutSheet
      open={open}
      onOpenChange={onOpenChange}
      sections={sections}
      {...(screenName ? { screenName } : {})}
    />
  );
}
